import { Client, validateAction } from '../common';
import { fromActionsResult, toActionsPayload, fromDeviceResult, fromSearchDevicesResult } from './dto';

export async function getActions(payload, options) {
  const data = await Client.request({
    method: 'POST',
    url: 'cm-service/actions',
    body: toActionsPayload(payload),
    ...options
  });

  return fromActionsResult(data);
}

export async function getAction(payload, options) {
  const { instanceId } = payload;

  return await Client.request({
    method: 'GET',
    url: `cm-service/actions/${instanceId}`,
    ...options
  });
}

export async function getDevice(payload, options) {
  const { deviceId } = payload;
  const result = await Client.request({
    method: 'GET',
    url: `cm-service/devices/${deviceId}`,
    ...options
  });

  return fromDeviceResult(result);
}

export async function searchDevicesV2(payload, options) {
  const { size, next, coalesceBundles, returnFields, filters, sort } = payload;
  const result = await Client.request({
    method: 'POST',
    url: `/cm-service/v2/devices/_search`,
    body: {
      size,
      next,
      coalesceBundles,
      returnFields,
      filters,
      sort
    },
    ...options
  });
  return fromSearchDevicesResult(result);
}

export async function rebootDevice(payload, options) {
  const { deviceId } = payload;

  return await Client.request({
    method: 'POST',
    url: `/cm-service/devices/${deviceId}/reboot`,
    body: {},
    validate: validateAction,
    ...options
  });
}

export async function lockDevice(payload, options) {
  const { deviceId, status } = payload;

  return await Client.request({
    method: 'POST',
    url: `/cm-service/devices/${deviceId}/remote_lock`,
    body: { status },
    ...options
  });
}

export async function addDeviceServiceUnit(payload, options) {
  const { deviceId, rfPath, serialId, neType } = payload;

  return await Client.request({
    method: 'POST',
    url: `/cm-service/devices/${deviceId}/provisionSu`,
    body: {
      'su-id': rfPath,
      'serial-id': serialId,
      'ne-type': neType
    }
  });
}

export async function removeDeviceServiceUnit(payload, options) {
  const { deviceId, rfPath } = payload;
  const searchParams = new URLSearchParams();
  searchParams.set('su-id', rfPath);
  const search = searchParams.toString();

  return await Client.request({
    method: 'DELETE',
    url: `/cm-service/devices/${deviceId}/provisionSu?${search}`,
    ...options
  });
}

export async function setDeviceServiceState(payload, options) {
  const { deviceId, rfPath, serviceRequest } = payload;

  return await Client.request({
    method: 'POST',
    url: `/cm-service/v2/devices/${deviceId}/setServiceState`,
    body: {
      'rf-path': rfPath,
      'service-request': serviceRequest
    },
    ...options
  });
}

export async function resetSectorGains(payload, options) {
  const { deviceId, sector } = payload;

  return await Client.request({
    method: 'POST',
    url: `/cm-service/devices/${deviceId}/deleteSectorProvData`,
    body: {
      sector: sector
    },
    ...options
  });
}

export async function setDeviceAntennaDirection(payload, options) {
  const { version, ...rest } = payload;

  switch (version) {
    case 'v2':
      return await setDeviceAntennaDirectionV2(rest, options);
    case 'v1':
    default:
      return await setDeviceAntennaDirectionV1(rest, options);
  }
}

export async function setDeviceAntennaDirectionV1(payload, options) {
  const { deviceId, direction } = payload;

  return await Client.request({
    method: 'POST',
    url: `/cm-service/devices/${deviceId}/antenna_direction`,
    body: { beamAzimuth: direction.azimuth, beamElevation: direction.elevation },
    ...options
  });
}

export async function setDeviceAntennaDirectionV2(payload, options) {
  const { deviceId, directions } = payload;

  return await Client.request({
    method: 'POST',
    url: `/cm-service/v2/devices/${deviceId}/antenna_direction`,
    body: directions,
    ...options
  });
}

export async function setDeviceAgcGainCaps(payload, options) {
  const { deviceId, networkElement, agcGainCaps } = payload;

  return await Client.request({
    method: 'POST',
    url: `/cm-service/devices/${deviceId}/gainCaps`,
    body: {
      'network-element': networkElement,
      ...agcGainCaps
    },
    ...options
  });
}

export async function setDeviceOscMitGain(payload, options) {
  const { deviceId, oscMitGain } = payload;

  return await Client.request({
    method: 'POST',
    url: `/cm-service/devices/${deviceId}/oscMitGain`,
    body: oscMitGain,
    ...options
  });
}

export async function setDeviceAgcDuration(payload, options) {
  const { deviceId, agcDuration } = payload;

  return await Client.request({
    method: 'POST',
    url: `/cm-service/devices/${deviceId}/agcDuration`,
    body: agcDuration,
    ...options
  });
}

export async function syncDevice(payload, options) {
  const { deviceId } = payload;

  return await Client.request({
    method: 'GET',
    url: `/cm-service/devices/${deviceId}/syncTwin`,
    ...options
  });
}

export async function setDeviceDefaultCableLoss(payload, options) {
  const { deviceId, defaultCableLoss, force, suId } = payload;

  return await Client.request({
    method: 'POST',
    url: `/cm-service/v2/devices/${deviceId}/defaultCableLoss`,
    body: {
      ...defaultCableLoss,
      force: force || false,
      suId: suId
    },
    ...options
  });
}
